/**
 * PAS region admission / Practice gate via AppFunctor → pas-math-pkp /admit.
 * Criteria: 快 / 短 / 锋利 / 闭目可想.
 * Admit also runs crop → qwen3.7-plus region vision before hatch judgment.
 */

import { apiClient } from './apiClient';

export type PasAdmissionRequest = {
  assetId: string;
  regionId: string;
  theme: string;
  sourceKpId?: string | null;
  /** Route AppFunctor → pas-{subject}-pkp FC. */
  subject?: string | null;
  cropRectNormalized: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
};

export type PasAdmissionResult = {
  admitted: boolean;
  reason?: string;
  warning?: string;
  practiceDraftId?: string;
  focusSkill?: string | null;
  principleScores?: {
    fast: number;
    short: number;
    sharp: number;
    eyes_closed: number;
  };
  /** Crop vision text for subsequent generate-one (avoid re-vision). */
  regionContent?: string;
  regionFeedback?: string;
  regionIncomplete?: boolean;
  regionMissingHints?: string[];
};

export async function requestPasRegionAdmission(
  input: PasAdmissionRequest,
): Promise<PasAdmissionResult> {
  const theme = String(input.theme || '').trim();
  if (theme.length < 2) {
    return {
      admitted: false,
      reason: 'theme_too_short',
    };
  }

  try {
    const payload = await apiClient.requestJson<{
      success?: boolean;
      data?: {
        admitted?: boolean;
        reason?: string;
        warning?: string;
        focusSkill?: string | null;
        principleScores?: PasAdmissionResult['principleScores'];
        regionContent?: string;
        regionFeedback?: string;
        regionIncomplete?: boolean;
        regionMissingHints?: string[];
      };
      error?: string;
      message?: string;
    }>(
      '/api/pas/math-pkp/admit',
      {
        method: 'POST',
        auth: true,
        body: {
          assetId: input.assetId,
          regionId: input.regionId,
          sourceKpId: input.sourceKpId ?? null,
          hatchPrompt: theme,
          theme,
          subject: input.subject || undefined,
          cropRectNormalized: input.cropRectNormalized,
        },
        // Vision + admit can exceed 30s on cold start.
        timeoutMs: 90_000,
      },
    );

    const admitted = Boolean(payload.success && payload.data?.admitted);
    // Soft gate for create: non-empty theme always proceeds; upstream reject → warning.
    const softAdmitted = admitted || theme.length >= 2;
    return {
      admitted: softAdmitted,
      reason: payload.data?.reason || payload.message || payload.error,
      warning: payload.data?.warning
        || (!admitted && softAdmitted
          ? (payload.data?.reason || '四原则压力偏高，仍可继续孵化')
          : undefined),
      focusSkill: payload.data?.focusSkill,
      principleScores: payload.data?.principleScores,
      regionContent: payload.data?.regionContent || '',
      regionFeedback: payload.data?.regionFeedback || '',
      regionIncomplete: Boolean(payload.data?.regionIncomplete),
      regionMissingHints: Array.isArray(payload.data?.regionMissingHints)
        ? payload.data!.regionMissingHints!
        : [],
      practiceDraftId: softAdmitted
        ? `practice-draft-${input.regionId}-${Date.now()}`
        : undefined,
    };
  } catch (error) {
    const err = error as Error & { payload?: unknown; statusCode?: number };
    const payload = (err.payload && typeof err.payload === 'object')
      ? err.payload as {
        message?: string;
        error?: string;
        data?: {
          admitted?: boolean;
          reason?: string;
          warning?: string;
          regionContent?: string;
          regionFeedback?: string;
          regionIncomplete?: boolean;
          regionMissingHints?: string[];
        };
      }
      : null;
    const themeOk = theme.length >= 2;
    return {
      admitted: themeOk,
      reason: payload?.data?.reason || payload?.message || err.message || 'admit_failed',
      warning: themeOk
        ? (payload?.data?.warning || payload?.data?.regionFeedback || '实战标注分析服务异常，已放行新建孵化（软警告）')
        : undefined,
      regionContent: payload?.data?.regionContent || '',
      regionFeedback: payload?.data?.regionFeedback || '',
      regionIncomplete: Boolean(payload?.data?.regionIncomplete),
      regionMissingHints: Array.isArray(payload?.data?.regionMissingHints)
        ? payload!.data!.regionMissingHints!
        : [],
      practiceDraftId: themeOk
        ? `practice-draft-${input.regionId}-${Date.now()}`
        : undefined,
    };
  }
}
